import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook: Dynamic Height & Scroll Lock
 * * What it does:
 * 1. Automatically calculates the exact distance from the top of the component to the bottom of the screen (window.innerHeight).
 * 2. Sets this height to the component so it perfectly fits the screen without any blank spaces at the bottom.
 * 3. Strictly locks the global page scroll (body and .ant-layout-content) to prevent double scrollbars.
 * 4. Automatically restores the scroll when the user navigates away from the tab/page (on unmount).
 *
 * @param defaultHeight Fallback height before JS calculates the exact pixels (default: '80vh')
 * @returns { containerRef, dynamicHeight }
 */
export const useDynamicHeightAndLockScroll = <
  T extends HTMLElement = HTMLDivElement
>(
  defaultHeight = '80vh'
): containerRef => {
  const containerRef = useRef<T>(null);
  const [dynamicHeight, setDynamicHeight] = useState<string>(defaultHeight);

  useEffect(() => {
    // 1. Dynamic Height Calculation
    const updateHeight = () => {
      if (containerRef.current) {
        // Get the element's coordinates relative to the viewport
        const rect = containerRef.current.getBoundingClientRect();

        // Subtract the distance from the top from the total window height
        const exactHeight = Math.floor(window.innerHeight - rect.top);
        setDynamicHeight(`${exactHeight}px`);
      }
    };

    // Run immediately and with a slight delay (fallback for rendering/animations)
    updateHeight();
    const timer = setTimeout(updateHeight, 100);

    // Recalculate on window resize
    window.addEventListener('resize', updateHeight);

    // 2. Global Scroll Lock
    // Find system wrappers that might cause global scrolling (e.g., Ant Design layout)
    const layoutContent = document.querySelector(
      '.ant-layout-content'
    ) as HTMLElement;

    // Store original overflow styles to restore them later
    const originalBodyOverflow = document.body.style.overflow;
    const originalLayoutOverflow = layoutContent
      ? layoutContent.style.overflow
      : '';

    // Strictly hide overflow to disable global scroll
    document.body.style.overflow = 'hidden';
    if (layoutContent) {
      layoutContent.style.overflow = 'hidden';
    }

    // 3. Cleanup on Unmount
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateHeight);

      // Restore original scroll styles for the page
      document.body.style.overflow = originalBodyOverflow;
      if (layoutContent) {
        layoutContent.style.overflow = originalLayoutOverflow;
      }
    };
  }, []);

  return { containerRef, dynamicHeight };
};

/* ==================================================================================
 * USAGE INSTRUCTIONS:
 * ==================================================================================
 * * IF YOU NEED TO CREATE ANOTHER FULL-SCREEN WITHOUT A GLOBAL SCROLL:
 * * 1. Import the hook:
 * import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';
 * * 2. Call it at the top of your component:
 * const { containerRef, dynamicHeight } = useDynamicHeightAndLockScroll();
 * * 3. Attach `ref` and `height` to the MAIN outer container (wrapper) of your component:
 * <div
 * ref={containerRef}
 * style={{
 * height: dynamicHeight,
 * display: 'flex',
 * flexDirection: 'column'
 * }}
 * >
 * ...your content...
 * </div>
 * * 4. MAKE INNER CONTAINERS FLEXIBLE (Flexbox Rule):
 * To ensure the inner list gets its own scrollbar, parent elements inside the wrapper
 * MUST have: `flex: 1` and `minHeight: 0`. The scrollable list container itself
 * should have `overflowY: 'auto'`.
 * * DONE! The hook will prevent double scrolling and stretch the container exactly to the bottom.
 * ==================================================================================
 */
